/**
 * The one reason line under a matchup board row (slice 8d), in words someone who doesn't know
 * football can read. It weighs the same two factors the board's win chance multiplies, from the
 * sim's own `strengths` and `physicalTypeMult`:
 *
 *   T = ln(type mult ratio), S = ln(matchup stat ratio), both from the offense's side
 *   Even when the win chance is within EVEN_BAND of a coin flip; otherwise the factor that pulls
 *   hardest toward the favored side names the reason: type ("Water beats Ground 1.52x", "Grass
 *   resists Electric") or stat ("Faster", "Stronger blocker", "Better arm", ...).
 *
 * The number is the multiplier the battle uses (slice 10a): the chart's, raised to
 * `types.battleExponent`, then the physical floor. The side it favors still follows the chart.
 */
import {
  balance,
  effectiveness,
  offenseWinChance,
  physicalTypeMult,
  strengths,
  type BoardKind,
  type Combatant,
  type TypeName,
} from '@tdmons/sim';

export type Side = 'offense' | 'defense';

/** A win chance this close to 50% reads "Even": a display band, not a balance number. */
export const EVEN_BAND = 0.05;

export interface EdgeReason {
  text: string;
  /** Who the reason favors, from the player's seat. */
  favors: 'you' | 'them' | 'even';
  factor: 'type' | 'stat' | 'even';
}

/** The battle kinds in plain words. */
export const KIND_LABEL: Record<BoardKind, string> = {
  block: 'Block',
  coverage: 'Coverage',
  tackle: 'Tackle',
  throw: 'Pass',
};

/** The stat reason by battle kind and the favored side (matchup.ts STRENGTH in words). */
const STAT_REASON: Record<BoardKind, Record<Side, string>> = {
  block: { offense: 'Stronger blocker', defense: 'Stronger rusher' },
  coverage: { offense: 'Faster', defense: 'Faster' },
  tackle: { offense: 'Breaks tackles', defense: 'Stronger tackler' },
  throw: { offense: 'Better arm', defense: 'Faster to the QB' },
};

const times = (m: number) => `${Number(m.toFixed(2))}x`;

/** The first of `xs` with the highest (or, `low`, lowest) score. */
function best<T>(xs: readonly T[], score: (x: T) => number, low = false): T {
  let out = xs[0] as T;
  let s = score(out);
  for (const x of xs.slice(1)) {
    const v = score(x);
    if (low ? v < s : v > s) [out, s] = [x, v];
  }
  return out;
}

/**
 * Why `win`'s types have the edge on `lose`'s: a super-effective hit, with the multiplier the
 * battle uses, or else a resist.
 */
export function typeReason(win: readonly TypeName[], lose: readonly TypeName[]): string {
  const hit = best(win, (t) => effectiveness(t, lose));
  if (effectiveness(hit, lose) > 1) {
    const used = physicalTypeMult(
      [hit],
      lose,
      balance.types.physicalFloor,
      balance.types.battleExponent,
    );
    return `${hit} beats ${best(lose, (u) => effectiveness(hit, [u]))} ${times(used)}`;
  }
  const theirs = best(lose, (u) => effectiveness(u, win));
  return `${best(win, (t) => effectiveness(theirs, [t]), true)} resists ${theirs}`;
}

/** The reason line for one board row, from the player's side (`player`). */
export function edgeReason(
  kind: BoardKind,
  offense: Combatant,
  defense: Combatant,
  player: Side,
): EdgeReason {
  const p = offenseWinChance(kind, offense, defense);
  if (Math.abs(p - 0.5) < EVEN_BAND) return { text: 'Even', favors: 'even', factor: 'even' };
  const favored: Side = p > 0.5 ? 'offense' : 'defense';
  const favors = favored === player ? 'you' : 'them';
  const { physicalFloor: floor, battleExponent: exponent } = balance.types;
  const positional = kind === 'throw';
  const tO = positional ? 1 : physicalTypeMult(offense.types, defense.types, floor, exponent);
  const tD = positional ? 1 : physicalTypeMult(defense.types, offense.types, floor, exponent);
  const s = strengths(kind, offense, defense);
  const sign = favored === 'offense' ? 1 : -1;
  const typePull = sign * Math.log(tO / tD);
  const statPull = sign * Math.log(s.offense / tO / (s.defense / tD));
  if (typePull > 0 && typePull >= statPull) {
    const [win, lose] = favored === 'offense' ? [offense, defense] : [defense, offense];
    return { text: typeReason(win.types, lose.types), favors, factor: 'type' };
  }
  return { text: STAT_REASON[kind][favored], favors, factor: 'stat' };
}
